<script setup>
// Where the voice prompt is in use and whether what is there is what is here. One line per voice agent bound to
// the assistant, with its state in words (the color only supports them), and one line about the latest version.
// An agent that keeps a copy of the prompt can drift from it; an agent that reads it on every turn cannot.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Badge } from 'dashboard/components-next/polaris/ui/badge';
import { Button } from 'dashboard/components-next/polaris/ui/button';

const props = defineProps({
  targets: { type: Array, required: true }, // from the state: [{ binding_id, agent_ref, brain }]
  drift: { type: Object, default: null }, // { state, targets: [{ binding_id, state }] }; null while unknown
  latestVersion: { type: Object, default: null },
  unversionedChanges: Boolean,
  checking: Boolean,
});
defineEmits(['recheck']);

const { t } = useI18n();

const VARIANTS = {
  in_sync: 'success',
  not_applicable: 'secondary',
  unpublished_changes: 'warning',
  never_published: 'warning',
  drifted: 'destructive',
  unavailable: 'outline',
  checking: 'outline',
};

const rows = computed(() =>
  props.targets.map(target => {
    const found = props.drift?.targets?.find(
      item => item.binding_id === target.binding_id
    );
    // The provider did not answer at all: every agent that keeps a copy is unknown for now.
    const fallback = props.drift ? 'unavailable' : 'checking';
    const state =
      found?.state ||
      (target.brain === 'polaris' ? 'not_applicable' : fallback);
    return {
      id: target.binding_id,
      agent: target.agent_ref,
      state,
      variant: VARIANTS[state] || 'outline',
      label: t(`POLARIS.ASSISTANTS.VOICE.DRIFT.${state.toUpperCase()}.LABEL`),
      help: t(`POLARIS.ASSISTANTS.VOICE.DRIFT.${state.toUpperCase()}.HELP`),
    };
  })
);

const versionLine = computed(() => {
  if (!props.latestVersion) {
    return t('POLARIS.ASSISTANTS.VOICE.STATUS.NO_VERSION');
  }
  const key = props.unversionedChanges ? 'VERSION_BEHIND' : 'VERSION_CURRENT';
  return t(`POLARIS.ASSISTANTS.VOICE.STATUS.${key}`, {
    version: props.latestVersion.version_no,
  });
});
</script>

<template>
  <p class="type-polaris-body" data-voice-version-line>{{ versionLine }}</p>
  <p
    v-if="rows.length === 0"
    class="type-polaris-body text-polaris-muted-foreground"
    data-voice-no-targets
  >
    {{ t('POLARIS.ASSISTANTS.VOICE.STATUS.NO_TARGETS') }}
  </p>
  <ul v-else class="m-0 grid list-none gap-3 p-0">
    <li
      v-for="row in rows"
      :key="row.id"
      class="grid gap-1"
      :data-voice-target="row.state"
    >
      <div class="flex flex-wrap items-center gap-2">
        <Badge :variant="row.variant">{{ row.label }}</Badge>
        <span class="min-w-0 break-all font-mono type-polaris-caption">
          {{ row.agent }}
        </span>
      </div>
      <p class="type-polaris-caption text-polaris-muted-foreground">
        {{ row.help }}
      </p>
    </li>
  </ul>
  <div v-if="rows.length">
    <Button
      variant="outline"
      size="sm"
      :disabled="checking"
      @click="$emit('recheck')"
    >
      {{
        checking
          ? t('POLARIS.ASSISTANTS.VOICE.STATUS.CHECKING')
          : t('POLARIS.ASSISTANTS.VOICE.STATUS.RECHECK')
      }}
    </Button>
  </div>
</template>
